import React, { useState } from 'react';
import { GlobeAltIcon, CheckCircleIcon, MagnifyingGlassIcon, BookOpenIcon, SparklesIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import { toast } from '../../utils/notification';
import MarkdownRenderer from '../../components/common/MarkdownRenderer';

const JurisdictionsTab = () => {
    const [loading, setLoading] = useState(false);
    const [researchLoading, setResearchLoading] = useState(false);
    const [activeSection, setActiveSection] = useState('research'); // 'research' or 'settings'

    // Jurisdiction settings
    const [jurisdictionSettings, setJurisdictionSettings] = useState({
        primary_jurisdiction: 'zimbabwe',
        secondary_jurisdiction: '',
        additional_jurisdictions: []
    });

    // Deep research state
    const [researchJurisdiction, setResearchJurisdiction] = useState('');
    const [researchQuery, setResearchQuery] = useState('');
    const [researchResults, setResearchResults] = useState(null);
    const [researchHistory, setResearchHistory] = useState([]);

    // Supported jurisdictions list grouped by region
    const supportedJurisdictions = [
        { value: 'zimbabwe', label: 'Zimbabwe', region: 'Southern Africa', flag: '🇿🇼', description: 'Roman-Dutch law influenced by English common law' },
        { value: 'south_africa', label: 'South Africa', region: 'Southern Africa', flag: '🇿🇦', description: 'Mixed legal system of Roman-Dutch civil law and English common law' },
        { value: 'botswana', label: 'Botswana', region: 'Southern Africa', flag: '🇧🇼', description: 'Roman-Dutch law with customary law elements' },
        { value: 'zambia', label: 'Zambia', region: 'Southern Africa', flag: '🇿🇲', description: 'English common law and customary law' },
        { value: 'malawi', label: 'Malawi', region: 'Southern Africa', flag: '🇲🇼', description: 'English common law with customary law' },
        { value: 'mozambique', label: 'Mozambique', region: 'Southern Africa', flag: '🇲🇿', description: 'Portuguese civil law system' },
        { value: 'namibia', label: 'Namibia', region: 'Southern Africa', flag: '🇳🇦', description: 'Roman-Dutch law with customary law' },
        { value: 'kenya', label: 'Kenya', region: 'East Africa', flag: '🇰🇪', description: 'English common law, Islamic law, and customary law' },
        { value: 'tanzania', label: 'Tanzania', region: 'East Africa', flag: '🇹🇿', description: 'English common law with Islamic and customary law' },
        { value: 'uganda', label: 'Uganda', region: 'East Africa', flag: '🇺🇬', description: 'English common law with customary law' },
        { value: 'rwanda', label: 'Rwanda', region: 'East Africa', flag: '🇷🇼', description: 'Mixed civil law and common law' },
        { value: 'nigeria', label: 'Nigeria', region: 'West Africa', flag: '🇳🇬', description: 'English common law, Islamic law in north, customary law' },
        { value: 'ghana', label: 'Ghana', region: 'West Africa', flag: '🇬🇭', description: 'English common law with customary law' },
        { value: 'uk', label: 'United Kingdom', region: 'Europe', flag: '🇬🇧', description: 'English common law system' },
        { value: 'usa', label: 'United States', region: 'Americas', flag: '🇺🇸', description: 'Federal and state common law systems' },
        { value: 'india', label: 'India', region: 'Asia', flag: '🇮🇳', description: 'Common law with Hindu and Islamic personal law' },
        { value: 'australia', label: 'Australia', region: 'Oceania', flag: '🇦🇺', description: 'English common law federal system' },
        { value: 'international', label: 'International Law', region: 'International', flag: '🌍', description: 'Public international law, treaties, and conventions' }
    ];

    // Group jurisdictions by region
    const jurisdictionsByRegion = supportedJurisdictions.reduce((acc, j) => {
        if (!acc[j.region]) acc[j.region] = [];
        acc[j.region].push(j);
        return acc;
    }, {});

    // Quick research topics for each jurisdiction
    const quickTopics = [
        'Constitutional Rights',
        'Criminal Law',
        'Contract Law',
        'Property Law',
        'Family Law',
        'Labor Law',
        'Corporate Law',
        'Environmental Law',
        'Human Rights',
        'Court System'
    ];

    const handleJurisdictionChange = (e) => {
        const { name, value } = e.target;
        setJurisdictionSettings({
            ...jurisdictionSettings,
            [name]: value
        });
    };

    const handleAddAdditionalJurisdiction = (value) => {
        if (value && !jurisdictionSettings.additional_jurisdictions.includes(value)) {
            setJurisdictionSettings({
                ...jurisdictionSettings,
                additional_jurisdictions: [...jurisdictionSettings.additional_jurisdictions, value]
            });
        }
    };

    const handleRemoveAdditionalJurisdiction = (value) => {
        setJurisdictionSettings({
            ...jurisdictionSettings,
            additional_jurisdictions: jurisdictionSettings.additional_jurisdictions.filter(j => j !== value)
        });
    };

    const handleSaveSettings = async () => {
        setLoading(true);
        try {
            await new Promise(resolve => setTimeout(resolve, 500));
            toast.success('Jurisdiction settings saved successfully');
        } catch (error) {
            console.error('Error saving jurisdiction settings:', error);
            toast.error('Failed to save jurisdiction settings');
        } finally {
            setLoading(false);
        }
    };

    // Determine if jurisdiction is African (supports ZimLII/AfricanLII search)
    const isAfricanJurisdiction = (jurisdictionValue) => {
        const africanJurisdictions = [
            'zimbabwe', 'south_africa', 'botswana', 'zambia', 'malawi',
            'mozambique', 'namibia', 'kenya', 'tanzania', 'uganda',
            'rwanda', 'nigeria', 'ghana'
        ];
        return africanJurisdictions.includes(jurisdictionValue);
    };

    const handleDeepResearch = async (e) => {
        e?.preventDefault();
        if (!researchJurisdiction) {
            toast.error('Please select a jurisdiction to research');
            return;
        }
        if (!researchQuery.trim()) {
            toast.error('Please enter a research query');
            return;
        }

        setResearchLoading(true);
        const jurisdiction = supportedJurisdictions.find(j => j.value === researchJurisdiction);

        let legalDatabaseResults = null;
        let aiAnalysis = '';

        try {
            // Step 1: Search AfricanLII/ZimLII for African jurisdictions
            if (isAfricanJurisdiction(researchJurisdiction)) {
                toast.info(`🔍 Searching legal databases for ${jurisdiction?.label} legal documents...`);

                try {
                    // Get auth token from localStorage
                    const token = localStorage.getItem('accessToken');
                    const headers = {
                        'Content-Type': 'application/json',
                    };
                    if (token) {
                        headers['Authorization'] = `Bearer ${token}`;
                    }

                    const zimliiResponse = await fetch('/api/research/queries/search-zimlii/', {
                        method: 'POST',
                        headers: headers,
                        body: JSON.stringify({
                            query: researchQuery,
                            jurisdiction: jurisdiction?.label || researchJurisdiction,
                            doc_type: '',
                            page_size: 15
                        }),
                    });

                    if (zimliiResponse.ok) {
                        legalDatabaseResults = await zimliiResponse.json();
                        console.log('ZimLII/AfricanLII results:', legalDatabaseResults);
                    }
                } catch (zimliiError) {
                    console.error('ZimLII search error:', zimliiError);
                    // Continue with AI research even if ZimLII fails
                }
            }

            // Step 2: Call AI research API for comprehensive analysis
            toast.info('🤖 Generating AI legal analysis...');

            // Build context from ZimLII results if available
            let contextMessage = `Research the following topic specifically in the context of ${jurisdiction?.label || researchJurisdiction} law: ${researchQuery}. Provide detailed information about relevant laws, statutes, legal principles, and notable cases if applicable.`;

            if (legalDatabaseResults && legalDatabaseResults.items && legalDatabaseResults.items.length > 0) {
                contextMessage += `\n\nI found the following relevant documents from official legal records:\n`;
                legalDatabaseResults.items.slice(0, 5).forEach((item, idx) => {
                    contextMessage += `\n${idx + 1}. ${item.title || 'Untitled'} (${item.type || 'document'})`;
                    if (item.citation) contextMessage += ` - ${item.citation}`;
                    if (item.date) contextMessage += ` [${item.date}]`;
                });
                contextMessage += `\n\nPlease analyze these documents and provide insights based on them.`;
            }

            const token = localStorage.getItem('accessToken');
            const aiHeaders = {
                'Content-Type': 'application/json',
            };
            if (token) {
                aiHeaders['Authorization'] = `Bearer ${token}`;
            }

            const response = await fetch('/api/research/queries/query_llm/', {
                method: 'POST',
                headers: aiHeaders,
                body: JSON.stringify({
                    query: contextMessage,
                    jurisdiction: researchJurisdiction
                }),
            });

            if (response.ok) {
                const data = await response.json();
                aiAnalysis = data.response || data.answer || data.message || '';
            }

            // Step 3: Build comprehensive result
            const result = {
                jurisdiction: jurisdiction,
                query: researchQuery,
                response: buildResearchResponse(jurisdiction?.label, researchQuery, legalDatabaseResults, aiAnalysis),
                timestamp: new Date().toISOString(),
                sources: legalDatabaseResults?.items || [],
                hasLegalDatabaseResults: !!(legalDatabaseResults?.items?.length > 0)
            };

            setResearchResults(result);
            setResearchHistory(prev => [result, ...prev.slice(0, 9)]);

            if (legalDatabaseResults?.items?.length > 0) {
                toast.success(`Found ${legalDatabaseResults.items.length} legal documents from official records`);
            }

        } catch (error) {
            console.error('Research error:', error);
            // Fallback to mock response
            const mockResult = {
                jurisdiction: jurisdiction,
                query: researchQuery,
                response: generateMockResponse(jurisdiction?.label, researchQuery),
                timestamp: new Date().toISOString(),
                sources: []
            };
            setResearchResults(mockResult);
            setResearchHistory(prev => [mockResult, ...prev.slice(0, 9)]);
        } finally {
            setResearchLoading(false);
        }
    };

    // Build a comprehensive response combining legal database results and AI analysis
    const buildResearchResponse = (jurisdictionName, query, legalDbResults, aiAnalysis) => {
        let response = `## ${jurisdictionName} Legal Research: ${query}\n\n`;

        // Add AfricanLII results section if available
        if (legalDbResults && legalDbResults.items && legalDbResults.items.length > 0) {
            response += `### 📚 Legal Database Results\n\n`;
            response += `Found **${legalDbResults.items.length}** relevant documents:\n\n`;

            // Categorize results
            const cases = legalDbResults.items.filter(item =>
                item.type === 'case' || item.type === 'judgment' || item.title?.toLowerCase().includes('v ')
            );
            const legislation = legalDbResults.items.filter(item =>
                item.type === 'legislation' || item.type === 'act' || item.type === 'statute'
            );
            const other = legalDbResults.items.filter(item =>
                !['case', 'judgment', 'legislation', 'act', 'statute'].includes(item.type) &&
                !item.title?.toLowerCase().includes('v ')
            );

            if (cases.length > 0) {
                response += `#### ⚖️ Cases & Judgments (${cases.length})\n`;
                cases.slice(0, 5).forEach((item, idx) => {
                    response += `${idx + 1}. **${item.title}**\n`;
                    if (item.citation) response += `   - Citation: ${item.citation}\n`;
                    if (item.court) response += `   - Court: ${item.court}\n`;
                    if (item.date) response += `   - Date: ${item.date}\n`;
                    if (item.url) response += `   - [View Source](${item.url})\n`;
                    response += '\n';
                });
            }

            if (legislation.length > 0) {
                response += `#### 📜 Legislation (${legislation.length})\n`;
                legislation.slice(0, 5).forEach((item, idx) => {
                    response += `${idx + 1}. **${item.title}**\n`;
                    if (item.year) response += `   - Year: ${item.year}\n`;
                    if (item.url) response += `   - [View Source](${item.url})\n`;
                    response += '\n';
                });
            }

            if (other.length > 0) {
                response += `#### 📄 Other Documents (${other.length})\n`;
                other.slice(0, 3).forEach((item, idx) => {
                    response += `${idx + 1}. **${item.title}**\n`;
                    if (item.url) response += `   - [View Source](${item.url})\n`;
                    response += '\n';
                });
            }

            response += `---\n\n`;
        }

        // Add AI analysis
        if (aiAnalysis && aiAnalysis.trim()) {
            response += `### 🤖 AI Legal Analysis\n\n`;
            response += aiAnalysis;
        } else {
            // Generate fallback analysis
            response += `### 🤖 AI Legal Analysis\n\n`;
            response += `#### Overview\n`;
            response += `This research pertains to **${query}** within the legal framework of **${jurisdictionName}**.\n\n`;
            response += `#### Key Legal Principles\n`;
            response += `1. **Constitutional Framework**: The legal system in ${jurisdictionName} provides specific provisions regarding ${query.toLowerCase()}.\n\n`;
            response += `2. **Statutory Law**: Relevant statutes and acts govern the application of ${query.toLowerCase()} in this jurisdiction.\n\n`;
            response += `3. **Case Law**: Various court decisions have shaped the interpretation and application of these laws.\n\n`;
            response += `#### Practical Considerations\n`;
            response += `When dealing with ${query.toLowerCase()} in ${jurisdictionName}, practitioners should consider:\n`;
            response += `- Jurisdictional requirements\n`;
            response += `- Procedural rules\n`;
            response += `- Time limitations and deadlines\n`;
            response += `- Available remedies\n\n`;
        }

        response += `---\n*Research generated on ${new Date().toLocaleString()}. Always verify with authoritative legal sources.*`;

        return response;
    };

    const generateMockResponse = (jurisdictionName, query) => {
        return `## ${jurisdictionName} Legal Research: ${query}

### Overview
This research pertains to **${query}** within the legal framework of **${jurisdictionName}**.

### Key Legal Principles
1. **Constitutional Framework**: The legal system in ${jurisdictionName} provides specific provisions regarding ${query.toLowerCase()}.

2. **Statutory Law**: Relevant statutes and acts govern the application of ${query.toLowerCase()} in this jurisdiction.

3. **Case Law**: Various court decisions have shaped the interpretation and application of these laws.

### Relevant Legislation
- Primary legislation relating to ${query.toLowerCase()}
- Subsidiary and delegated legislation
- International treaties and conventions (where applicable)

### Practical Considerations
When dealing with ${query.toLowerCase()} in ${jurisdictionName}, practitioners should consider:
- Jurisdictional requirements
- Procedural rules
- Time limitations and deadlines
- Available remedies

### Next Steps
For comprehensive legal advice specific to your situation, please consult with a qualified legal practitioner licensed in ${jurisdictionName}.

---
*This is an AI-generated research summary. Always verify with authoritative legal sources.*`;
    };

    const handleQuickTopic = (topic) => {
        setResearchQuery(topic);
    };

    const getJurisdictionLabel = (value) => {
        const j = supportedJurisdictions.find(j => j.value === value);
        return j ? `${j.flag} ${j.label}` : value;
    };

    const getSelectedJurisdiction = () => {
        return supportedJurisdictions.find(j => j.value === researchJurisdiction);
    };

    return (
        <div className="space-y-6">
            {/* Header with Section Toggle */}
            <div className="bg-gradient-to-r from-primary-600 to-primary-800 rounded-lg shadow-lg p-6 text-white">
                <div className="flex items-center justify-between">
                    <div className="flex items-center">
                        <GlobeAltIcon className="h-10 w-10 mr-4" />
                        <div>
                            <h2 className="text-xl font-bold">Jurisdiction Research Center</h2>
                            <p className="text-primary-100 mt-1">
                                Explore legal systems worldwide and configure your jurisdiction preferences
                            </p>
                        </div>
                    </div>
                </div>

                {/* Section Toggle */}
                <div className="mt-4 flex space-x-2">
                    <button
                        onClick={() => setActiveSection('research')}
                        className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${activeSection === 'research'
                            ? 'bg-white text-primary-700'
                            : 'bg-primary-700 text-white hover:bg-primary-600'
                            }`}
                    >
                        <MagnifyingGlassIcon className="h-4 w-4 inline mr-2" />
                        Deep Research
                    </button>
                    <button
                        onClick={() => setActiveSection('settings')}
                        className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${activeSection === 'settings'
                            ? 'bg-white text-primary-700'
                            : 'bg-primary-700 text-white hover:bg-primary-600'
                            }`}
                    >
                        <CheckCircleIcon className="h-4 w-4 inline mr-2" />
                        Preferences
                    </button>
                </div>
            </div>

            {/* Deep Research Section */}
            {activeSection === 'research' && (
                <>
                    {/* Research Interface */}
                    <div className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden">
                        <div className="px-6 py-5 border-b border-gray-200 dark:border-gray-700">
                            <div className="flex items-center">
                                <SparklesIcon className="h-6 w-6 text-primary-500 mr-2" />
                                <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                                    Deep Jurisdiction Research
                                </h3>
                            </div>
                            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                                Explore any legal jurisdiction in depth. Ask questions about laws, legal systems, court structures, and more.
                            </p>
                        </div>

                        <form onSubmit={handleDeepResearch} className="px-6 py-5 space-y-4">
                            {/* Jurisdiction Selection */}
                            <div>
                                <label htmlFor="research_jurisdiction" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                    Select Jurisdiction to Research
                                </label>
                                <select
                                    id="research_jurisdiction"
                                    value={researchJurisdiction}
                                    onChange={(e) => setResearchJurisdiction(e.target.value)}
                                    className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 dark:border-gray-600 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm rounded-md dark:bg-gray-700 dark:text-white"
                                >
                                    <option value="">Choose a jurisdiction...</option>
                                    {Object.entries(jurisdictionsByRegion).map(([region, jurisdictions]) => (
                                        <optgroup key={region} label={region}>
                                            {jurisdictions.map(j => (
                                                <option key={j.value} value={j.value}>{j.flag} {j.label}</option>
                                            ))}
                                        </optgroup>
                                    ))}
                                </select>
                            </div>

                            {/* Selected Jurisdiction Info */}
                            {researchJurisdiction && (
                                <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                                    <div className="flex items-start">
                                        <span className="text-3xl mr-3">{getSelectedJurisdiction()?.flag}</span>
                                        <div>
                                            <h4 className="font-medium text-gray-900 dark:text-white">
                                                {getSelectedJurisdiction()?.label}
                                            </h4>
                                            <p className="text-sm text-gray-500 dark:text-gray-400">
                                                {getSelectedJurisdiction()?.description}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Quick Topics */}
                            {researchJurisdiction && (
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        Quick Research Topics
                                    </label>
                                    <div className="flex flex-wrap gap-2">
                                        {quickTopics.map(topic => (
                                            <button
                                                key={topic}
                                                type="button"
                                                onClick={() => handleQuickTopic(topic)}
                                                className={`px-3 py-1 text-sm rounded-full border transition-colors ${researchQuery === topic
                                                    ? 'bg-primary-100 border-primary-300 text-primary-700 dark:bg-primary-900 dark:border-primary-700 dark:text-primary-200'
                                                    : 'bg-gray-100 border-gray-300 text-gray-700 hover:bg-gray-200 dark:bg-gray-600 dark:border-gray-500 dark:text-gray-200 dark:hover:bg-gray-500'
                                                    }`}
                                            >
                                                {topic}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Research Query */}
                            <div>
                                <label htmlFor="research_query" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                    Research Query
                                </label>
                                <div className="mt-1 relative">
                                    <textarea
                                        id="research_query"
                                        value={researchQuery}
                                        onChange={(e) => setResearchQuery(e.target.value)}
                                        rows={3}
                                        placeholder="Enter your legal research question... e.g., 'What are the requirements for forming a valid contract?' or 'Explain the court hierarchy and appeals process.'"
                                        className="block w-full px-4 py-3 border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
                                    />
                                </div>
                            </div>

                            {/* Submit Button */}
                            <div className="flex justify-end">
                                <button
                                    type="submit"
                                    disabled={researchLoading || !researchJurisdiction || !researchQuery.trim()}
                                    className={`inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 ${(researchLoading || !researchJurisdiction || !researchQuery.trim()) ? 'opacity-50 cursor-not-allowed' : ''
                                        }`}
                                >
                                    {researchLoading ? (
                                        <>
                                            <ArrowPathIcon className="animate-spin h-5 w-5 mr-2" />
                                            Researching...
                                        </>
                                    ) : (
                                        <>
                                            <MagnifyingGlassIcon className="h-5 w-5 mr-2" />
                                            Start Deep Research
                                        </>
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>

                    {/* Research Results */}
                    {researchResults && (
                        <div className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden">
                            <div className="px-6 py-5 border-b border-gray-200 dark:border-gray-700">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center">
                                        <BookOpenIcon className="h-6 w-6 text-green-500 mr-2" />
                                        <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                                            Research Results
                                        </h3>
                                    </div>
                                    <span className="text-sm text-gray-500 dark:text-gray-400">
                                        {researchResults.jurisdiction?.flag} {researchResults.jurisdiction?.label}
                                    </span>
                                </div>
                                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                                    Query: "{researchResults.query}"
                                </p>
                            </div>
                            <div className="px-6 py-5">
                                <MarkdownRenderer content={researchResults.response} />
                            </div>
                        </div>
                    )}

                    {/* Research History */}
                    {researchHistory.length > 0 && (
                        <div className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden">
                            <div className="px-6 py-5 border-b border-gray-200 dark:border-gray-700">
                                <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                                    Recent Research
                                </h3>
                            </div>
                            <ul className="divide-y divide-gray-200 dark:divide-gray-700">
                                {researchHistory.slice(0, 5).map((item, idx) => (
                                    <li
                                        key={idx}
                                        className="px-6 py-4 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer"
                                        onClick={() => {
                                            setResearchJurisdiction(item.jurisdiction?.value || '');
                                            setResearchQuery(item.query);
                                            setResearchResults(item);
                                        }}
                                    >
                                        <div className="flex items-center justify-between">
                                            <div className="flex items-center">
                                                <span className="text-xl mr-3">{item.jurisdiction?.flag}</span>
                                                <div>
                                                    <p className="text-sm font-medium text-gray-900 dark:text-white">
                                                        {item.query}
                                                    </p>
                                                    <p className="text-xs text-gray-500 dark:text-gray-400">
                                                        {item.jurisdiction?.label}
                                                    </p>
                                                </div>
                                            </div>
                                            <span className="text-xs text-gray-400">
                                                {new Date(item.timestamp).toLocaleTimeString()}
                                            </span>
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}

                    {/* Jurisdiction Cards Grid */}
                    <div className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden">
                        <div className="px-6 py-5 border-b border-gray-200 dark:border-gray-700">
                            <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                                Explore Jurisdictions
                            </h3>
                            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                                Click on any jurisdiction to start researching
                            </p>
                        </div>
                        <div className="p-6">
                            {Object.entries(jurisdictionsByRegion).map(([region, jurisdictions]) => (
                                <div key={region} className="mb-6 last:mb-0">
                                    <h4 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-3">
                                        {region}
                                    </h4>
                                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                                        {jurisdictions.map(j => (
                                            <button
                                                key={j.value}
                                                onClick={() => {
                                                    setResearchJurisdiction(j.value);
                                                    window.scrollTo({ top: 0, behavior: 'smooth' });
                                                }}
                                                className={`p-3 rounded-lg border text-left transition-all hover:shadow-md ${researchJurisdiction === j.value
                                                    ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                                                    : 'border-gray-200 dark:border-gray-600 hover:border-primary-300'
                                                    }`}
                                            >
                                                <span className="text-2xl">{j.flag}</span>
                                                <p className="mt-1 font-medium text-gray-900 dark:text-white text-sm">
                                                    {j.label}
                                                </p>
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </>
            )}

            {/* Settings Section */}
            {activeSection === 'settings' && (
                <>
                    {/* Main Settings Card */}
                    <div className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden">
                        <div className="px-6 py-5 border-b border-gray-200 dark:border-gray-700">
                            <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                                Default Jurisdiction Preferences
                            </h3>
                            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                                Configure your default jurisdictions for case-based research and document generation.
                            </p>
                        </div>

                        <div className="px-6 py-5 space-y-6">
                            {/* Primary Jurisdiction */}
                            <div>
                                <label htmlFor="primary_jurisdiction" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                    Primary Jurisdiction <span className="text-red-500">*</span>
                                </label>
                                <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                                    Your main legal jurisdiction for research and document generation
                                </p>
                                <select
                                    id="primary_jurisdiction"
                                    name="primary_jurisdiction"
                                    value={jurisdictionSettings.primary_jurisdiction}
                                    onChange={handleJurisdictionChange}
                                    className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 dark:border-gray-600 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm rounded-md dark:bg-gray-700 dark:text-white"
                                >
                                    <option value="">Select a jurisdiction</option>
                                    {Object.entries(jurisdictionsByRegion).map(([region, jurisdictions]) => (
                                        <optgroup key={region} label={region}>
                                            {jurisdictions.map(j => (
                                                <option key={j.value} value={j.value}>{j.flag} {j.label}</option>
                                            ))}
                                        </optgroup>
                                    ))}
                                </select>
                            </div>

                            {/* Secondary Jurisdiction */}
                            <div>
                                <label htmlFor="secondary_jurisdiction" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                    Secondary Jurisdiction
                                </label>
                                <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                                    Optional: A secondary jurisdiction for comparative legal research
                                </p>
                                <select
                                    id="secondary_jurisdiction"
                                    name="secondary_jurisdiction"
                                    value={jurisdictionSettings.secondary_jurisdiction}
                                    onChange={handleJurisdictionChange}
                                    className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 dark:border-gray-600 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm rounded-md dark:bg-gray-700 dark:text-white"
                                >
                                    <option value="">None</option>
                                    {Object.entries(
                                        supportedJurisdictions
                                            .filter(j => j.value !== jurisdictionSettings.primary_jurisdiction)
                                            .reduce((acc, j) => {
                                                if (!acc[j.region]) acc[j.region] = [];
                                                acc[j.region].push(j);
                                                return acc;
                                            }, {})
                                    ).map(([region, jurisdictions]) => (
                                        <optgroup key={region} label={region}>
                                            {jurisdictions.map(j => (
                                                <option key={j.value} value={j.value}>{j.flag} {j.label}</option>
                                            ))}
                                        </optgroup>
                                    ))}
                                </select>
                            </div>

                            {/* Additional Jurisdictions */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                    Additional Jurisdictions
                                </label>
                                <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                                    Optional: Add more jurisdictions for multi-jurisdictional research
                                </p>

                                {jurisdictionSettings.additional_jurisdictions.length > 0 && (
                                    <div className="flex flex-wrap gap-2 mb-3">
                                        {jurisdictionSettings.additional_jurisdictions.map(jValue => {
                                            const jurisdiction = supportedJurisdictions.find(j => j.value === jValue);
                                            return (
                                                <span
                                                    key={jValue}
                                                    className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-primary-100 text-primary-800 dark:bg-primary-900 dark:text-primary-200"
                                                >
                                                    {jurisdiction?.flag} {jurisdiction?.label || jValue}
                                                    <button
                                                        type="button"
                                                        onClick={() => handleRemoveAdditionalJurisdiction(jValue)}
                                                        className="ml-2 inline-flex items-center justify-center w-4 h-4 rounded-full text-primary-400 hover:bg-primary-200 hover:text-primary-600 dark:hover:bg-primary-800 focus:outline-none"
                                                    >
                                                        <span className="sr-only">Remove {jurisdiction?.label}</span>
                                                        <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                                            <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                                                        </svg>
                                                    </button>
                                                </span>
                                            );
                                        })}
                                    </div>
                                )}

                                <select
                                    onChange={(e) => {
                                        handleAddAdditionalJurisdiction(e.target.value);
                                        e.target.value = '';
                                    }}
                                    className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 dark:border-gray-600 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm rounded-md dark:bg-gray-700 dark:text-white"
                                >
                                    <option value="">Add a jurisdiction...</option>
                                    {Object.entries(
                                        supportedJurisdictions
                                            .filter(j =>
                                                j.value !== jurisdictionSettings.primary_jurisdiction &&
                                                j.value !== jurisdictionSettings.secondary_jurisdiction &&
                                                !jurisdictionSettings.additional_jurisdictions.includes(j.value)
                                            )
                                            .reduce((acc, j) => {
                                                if (!acc[j.region]) acc[j.region] = [];
                                                acc[j.region].push(j);
                                                return acc;
                                            }, {})
                                    ).map(([region, jurisdictions]) => (
                                        <optgroup key={region} label={region}>
                                            {jurisdictions.map(j => (
                                                <option key={j.value} value={j.value}>{j.flag} {j.label}</option>
                                            ))}
                                        </optgroup>
                                    ))}
                                </select>
                            </div>
                        </div>

                        {/* Save Button */}
                        <div className="px-6 py-4 bg-gray-50 dark:bg-gray-700 border-t border-gray-200 dark:border-gray-600">
                            <button
                                type="button"
                                onClick={handleSaveSettings}
                                disabled={loading}
                                className={`inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 ${loading ? 'opacity-70 cursor-not-allowed' : ''}`}
                            >
                                {loading ? (
                                    <>
                                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                        </svg>
                                        Saving...
                                    </>
                                ) : (
                                    <>
                                        <CheckCircleIcon className="h-4 w-4 mr-2" />
                                        Save Preferences
                                    </>
                                )}
                            </button>
                        </div>
                    </div>

                    {/* Current Selection Summary */}
                    <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
                        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                            Current Preferences Summary
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                                <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">Primary</p>
                                <p className="mt-1 text-lg font-semibold text-gray-900 dark:text-white">
                                    {jurisdictionSettings.primary_jurisdiction
                                        ? getJurisdictionLabel(jurisdictionSettings.primary_jurisdiction)
                                        : 'Not selected'}
                                </p>
                            </div>
                            <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                                <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">Secondary</p>
                                <p className="mt-1 text-lg font-semibold text-gray-900 dark:text-white">
                                    {jurisdictionSettings.secondary_jurisdiction
                                        ? getJurisdictionLabel(jurisdictionSettings.secondary_jurisdiction)
                                        : 'None'}
                                </p>
                            </div>
                            <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                                <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">Additional</p>
                                <p className="mt-1 text-lg font-semibold text-gray-900 dark:text-white">
                                    {jurisdictionSettings.additional_jurisdictions.length > 0
                                        ? `${jurisdictionSettings.additional_jurisdictions.length} selected`
                                        : 'None'}
                                </p>
                            </div>
                        </div>
                    </div>
                </>
            )}

            {/* Info Box */}
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg p-4">
                <div className="flex">
                    <div className="flex-shrink-0">
                        <svg className="h-5 w-5 text-blue-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                        </svg>
                    </div>
                    <div className="ml-3">
                        <h3 className="text-sm font-medium text-blue-800 dark:text-blue-200">
                            {activeSection === 'research' ? 'About Deep Research' : 'How Jurisdictions Affect Your Work'}
                        </h3>
                        <div className="mt-2 text-sm text-blue-700 dark:text-blue-300">
                            {activeSection === 'research' ? (
                                <ul className="list-disc pl-5 space-y-1">
                                    <li>Deep research lets you explore any jurisdiction without creating a case</li>
                                    <li>Use quick topics or enter custom queries for in-depth analysis</li>
                                    <li>Research history is saved for quick reference during your session</li>
                                    <li>Click any jurisdiction card below to start exploring</li>
                                </ul>
                            ) : (
                                <ul className="list-disc pl-5 space-y-1">
                                    <li>Your primary jurisdiction determines which legal databases are searched first</li>
                                    <li>AI responses will prioritize laws and precedents from your selected jurisdictions</li>
                                    <li>Document templates will use the appropriate legal format for your jurisdiction</li>
                                    <li>Comparative research will highlight differences between your selected jurisdictions</li>
                                </ul>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default JurisdictionsTab;

